<template>
  <ProvideTags v-slot="{ tags }" :locale="localeIn" :tag-type="tagType" :include-id="includeId">
    <div>
      <div :class="{ 'input-group input-group-lg added-button': addSearchButton }">
        <VSelect
          v-model="selected"
          v-model:selected="selected"
          :value="selectedIn"
          :classes="searchBarClasses"
          :placeholder="placeholder"
          :inline="addSearchButton"
          :options="tags"
          :input-id="name"
          :name="name"
          :delete-tags="allowDelete"
          :form-id="formId"
          :max="max"
          :on-change="change"
          :label="label"
          :show-search-button="false"
          :no-drop="!showDrop"
          :can-add-unsupported-options="canAddUnsupportedTypes"
          :disabled="disabled"
          :auto-select-on-blur="autoSelectOnBlur"
          multiple
          @option-deselected="handleOptionDeselected"
          @error="inputError = $event"
        />
        <a
          v-if="addSearchButton"
          href="#"
          class="btn-success input-group-addon z-depth-0 waves-effect"
        >
          <span class="glyphicon glyphicon-search" aria-hidden="true" />
          &nbsp;
          {{ $t('Search') }}
        </a>
      </div>
      <div v-if="inputError" class="tw-text-red-500">{{ inputError }}</div>
      <div class="f-12 c-gray">
        <strong>
          {{
            max === -1
              ? $t('Add by pressing enter or comma.')
              : $t('Add by pressing enter or comma. Maximum :max characters per item.', {
                  max: max,
                })
          }}
        </strong>
      </div>
      <TypeSuggestions
        v-if="showTypesuggestions"
        ref="suggestions"
        :item-added="itemAddedFromSuggestion"
        :skills-list="preListSuggestions"
        :filter-skills="selected"
        :inline="addSearchButton"
      />
    </div>
  </ProvideTags>
</template>

<script>
import TypeSuggestions from '@/components/TypeSuggestions.vue'
import ProvideTags from '@/components/ProvideTags.vue'
import VSelect from '@/components/VSelect.vue'

export default {
  name: 'AppTypeSelect',
  components: { VSelect, TypeSuggestions, ProvideTags },
  props: {
    tagType: {
      type: String,
      required: true,
      validator(type) {
        return ['skills', 'industries', 'jobTitles', 'emails', 'fileTags'].includes(type)
      },
    },
    searchBarClasses: { type: Object, default: () => {} },
    formId: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showTypesuggestions: { type: Boolean, default: true },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    canAddUnsupportedTypes: { type: Boolean, default: true },
    name: { type: String, required: true },
    selectedIn: { type: Array, default: () => [] },
    initialCategory: { type: String, default: '' },
    preListSuggestions: { type: Array, default: () => [] },
    placeholder: { type: String, default: $t('Javascript, Excel, SEO') },
    localeIn: { type: String, default: '' },
    optionsIn: { type: [Array, null], default: () => null },
    max: { type: Number, default: 50 }, // give it -1 if you want unlimited character
    allowDelete: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showDrop: { type: Boolean, default: true },
    change: { type: Function, default() {} },
    addSearchButton: { type: Boolean, default: false },
    /**
     * Whether to include id and name in the query result ({id: 'some-id', name: 'tag name'}) or just a
     * flattened array of names (default)
     */
    includeId: { type: Boolean, default: false },
    /**
     * Tells vue-select what key to use when generating option
     * labels when each `option` is an object.
     */
    label: { type: String, default: 'label' },
    /**
     * When true, the component will automatically select the value when the input loses focus
     * if there's only one value and no pre-selected options.
     */
    autoSelectOnBlur: { type: Boolean, default: false },
  },
  emits: ['selection-changed'],
  data() {
    return {
      selected: [],
      userDeselected: [],
      inputError: '',
    }
  },
  watch: {
    selectedIn: {
      deep: true,
      handler(toAdd) {
        // Only add items through the prop which have not been actively removed by user already
        const toAddFromProps = toAdd.filter(
          (x) => !this.userDeselected.includes(x) && !this.selected.includes(x),
        )
        if (toAddFromProps.length > 0) {
          this.selected = this.selected.concat(toAddFromProps)
        }
      },
    },
    selected: {
      deep: true,
      handler() {
        if (this.$refs.suggestions) {
          this.$refs.suggestions.suggestSkills(this.selected[this.selected.length - 1])
        }
        this.$emit('selection-changed', this.selected)
      },
    },
    initialCategory() {
      if (this.$refs.suggestions) {
        this.selected = []
        this.$refs.suggestions.suggestSkills(this.initialCategory)
      }
    },
  },
  created() {
    this.selected = this.selectedIn

    if (this.initialCategory && this.$refs.suggestions) {
      this.$refs.suggestions.suggestSkills(this.initialCategory)
    }
  },
  methods: {
    itemAddedFromSuggestion(item) {
      if (item.length > 0) {
        if (this.selected.find((i) => i === item)) {
          // Send growler message here
        } else {
          this.selected.push(item)
        }
      }
    },
    handleOptionDeselected(option) {
      this.userDeselected = [...new Set([...this.userDeselected, option])]
    },
  },
}
</script>

<style scoped lang="scss">
.added-button {
  :deep(.bootstrap-tagsinput) {
    background: #fff;
    border: 1px solid #ccc;
    border-width: 0 0 1px 0;
    border-radius: 0 6px 0 6px;
    padding-left: 10px;
    width: 100%;
    .twitter-typeahead {
      width: auto;
      vertical-align: middle;

      .form-control.tt-input {
        width: 100%;
        border: none;
        font-size: 16px;
        line-height: 1;
        border-radius: 0 6px 0 6px;
        background: none;
        min-height: 46px;
        vertical-align: inherit;
        min-width: 260px;
      }
    }
    .tag {
      background-color: #32e0b9;
      font-weight: 400;
      font-size: 13px;
      padding: 6px 10px 6px;
      text-transform: uppercase;
      margin: 11px 0 0 0px;
    }
  }
}
</style>
